import React from 'react';

import {
  LTLoader,
  LTTestStatusLabel,
  LTText,
  LTTooltip
} from '@lambdatestincprivate/lt-components';
import { ClockIcon } from '@primer/octicons-react';

import { getStatusIconAndClassPrefix } from '../../utils/statusOptions';
import { convertTimeTaken, timeDifference } from '../../utils/dateTimeUtils';
import styles from './styles.module.css';

const getDuration = (test) => {
  try {
    if (test?.duration) {
      return convertTimeTaken(test.duration);
    }
    if (test?.start_timestamp && test?.end_timestamp) {
      return convertTimeTaken(
        timeDifference(test.start_timestamp, test.end_timestamp, true)
      );
    }
    if (test?.startedAt && test?.endedAt) {
      return convertTimeTaken(
        timeDifference(test.startedAt, test.endedAt, true)
      );
    }
    return '';
  } catch {
    return '';
  }
};

const SidebarTestItem = React.forwardRef(
  (
    {
      test,
      selectedTestId,
      onClick,
      style,
      className,
      'data-index': dataIndex,
      'data-testid': dataTestId
    },
    ref
  ) => {
    const name = test?.name || test?.id || '';
    const capability = test?.caps || '';
    let capabilityJson = {};
    try {
      capabilityJson = capability ? JSON.parse(capability) : {};
    } catch {
      capabilityJson = {};
    }
    const tcId = capabilityJson?.desiredCapabilities?.['tms.tc_id'];
    const status = test?.status || '';
    const duration = getDuration(test);
    const isSelected = selectedTestId
      ? test?.id === selectedTestId || test?.test_id === selectedTestId
      : false;

    const { statusIconName, statusName } =
      getStatusIconAndClassPrefix(status) || {};

    const handleKeyDown = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick?.();
      }
    };

    return (
      <div
        ref={ref}
        role="option"
        aria-selected={isSelected}
        aria-label={`${name || 'Unnamed test'}, ${
          statusName || 'unknown'
        } status`}
        tabIndex={0}
        className={`${styles.testCard} ${className || ''}`}
        style={style}
        data-index={dataIndex}
        data-testid={dataTestId}
        onClick={onClick}
        onKeyDown={handleKeyDown}
      >
        {isSelected && (
          <div aria-hidden="true" className={styles.selectedIndicator} />
        )}
        <div
          className={`${styles.testItem} ${
            isSelected ? styles.selectedTest : ''
          }`}
        >
          {/* Status icon */}
          <div className={styles.testItemIcon}>
            {statusIconName ? (
              <LTTooltip
                text={statusName}
                direction={LTTooltip.Direction.RIGHT}
              >
                <span aria-hidden="true">
                  <LTTestStatusLabel
                    iconSrc={statusIconName}
                    status={status}
                    showStatusText={false}
                    width={16}
                    height={16}
                  />
                </span>
              </LTTooltip>
            ) : (
              <div style={{ height: 16, width: 16 }}>
                <LTLoader
                  containerStyles={{ padding: 0 }}
                  size={LTLoader.SpinnerSizes.SMALL}
                  spinnerStyles={{
                    height: 16,
                    width: 16,
                    color: '#0366D6',
                    fill: '#0366D6',
                    fillOpacity: '0.1'
                  }}
                />
              </div>
            )}
          </div>

          {/* Text content */}
          <div className={styles.testItemContent}>
            <LTText
              variant={LTText.TextVariant.BODY_REGULAR}
              color="fg.default"
              className={styles.testItemName}
              style={{
                fontSize: 14,
                lineHeight: '20px',
                letterSpacing: '-0.15px'
              }}
            >
              {name || test?.id}
            </LTText>

            {(tcId || duration) && (
              <div className={`${styles.testItemSubtitleRow}`}>
                {tcId && (
                  <LTText
                    variant={LTText.TextVariant.SMALL_REGULAR}
                    color="fg.muted"
                  >
                    #{tcId}
                  </LTText>
                )}
                {tcId && duration && (
                  <span aria-hidden="true" className={styles.subtitleDot} />
                )}
                {duration && (
                  <span className={styles.testItemDuration}>
                    <ClockIcon size={16} />
                    <LTText
                      variant={LTText.TextVariant.SMALL_REGULAR}
                      color="fg.muted"
                    >
                      {duration}
                    </LTText>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

SidebarTestItem.displayName = 'SidebarTestItem';

export default SidebarTestItem;
